// The product form of the Products page: the form's values, the checks made before the impact preview,
// and the product the API receives (Events checks it again, common/logistics).

// The catalogue's lists and limits, as sportya-api common/logistics checks them. Tenis10 is a sport of the catalogue, as on
// „Create event” (PRD 16 LR15, the canvas verdict C-A): its products name the colours they follow; the lanyards are its own family.
export const FAMILIES = ["balls", "medals", "diplomas", "water", "lanyards", "other"]
// A `box` is a can of balls; water counts bottles, no longer litres (Tudor, 2026-10-13); the club's Tenis10 balls count balls.
export const UNITS = ["box", "pcs", "set", "bottle"]
export const TENIS10 = "tenis10"
export const SPORTS = ["tennis", "padel", TENIS10]
// The Tenis10 colours in the programme's order; only a Tenis10 ball or lanyard follows some of them.
export const COLOURS = ["rosu", "portocaliu", "verde", "galben"]
export const COLOUR_FAMILIES = ["balls", "lanyards"]
export const GAME_TYPES = ["singles", "doubles"]
export const NAME_LIMIT = 120
export const PACKAGE_NAME_LIMIT = 40
const LIMITS = { perUnit: 10000, packageUnits: 100000, weight: 1000, quantity: 1000000, factor: 100, reserve: 100 }
// The predefined rules (PRD R1–R7, R8 for the club's Tenis10 balls); none is a free formula.
export const RULES = {
  perNewBallMatch: "R1 · Per new-ball match", perPlace: "R2 · Per champion and finalist", percentOf: "R3 · Percentage of another product",
  perParticipant: "R4 · Per participant", perCompetition: "R5 · Per competition", fixed: "R6 · Fixed per event", perPlayerPerMatch: "R7 · Per player per match",
  perGroup: "R8 · Per group",
}
// The rules counted in a quantity of the product's unit, with the label of that quantity.
export const QUANTITY_RULES = { perParticipant: "Quantity per participant", perCompetition: "Quantity per competition", fixed: "Quantity per event",
  perPlayerPerMatch: "Quantity per player per match", perGroup: "Quantity per group" }
export const GAME_TYPE_RULES = ["perParticipant", "perCompetition", "perPlayerPerMatch"]
export const PLACES_LIMIT = 8

// Whether the form holds a Tenis10 product, and one that follows colours (a ball or a lanyard); a ball of Tenis10 alone counts balls.
export const isTenis10Form = form => form.sport === TENIS10
export const followsColours = form => isTenis10Form(form) && COLOUR_FAMILIES.includes(form.family)
export const unitsOf = form => isTenis10Form(form) && form.family === "balls" ? [...UNITS, "ball"] : UNITS
// R8 · Per group is the club's Tenis10 balls of a colour: the minimum to run the event, one group per court (Events ADR 0016).
export const groupsRule = form => isTenis10Form(form) && form.family === "balls"

export const emptyForm = () => ({
  name: "", en: "", bg: "", family: "other", sport: "all", allCategories: true, categories: [], officialBalls: [], tenis10Levels: [],
  unit: "pcs", perUnit: "1", groupingName: "", groupingUnits: "", unitWeight: "",
  supplier: "mpg", minimum: "", expiresOn: "",
  ruleType: "", places: "2", reference: "", factor: "", quantity: "", gameType: "", reserve: "0", active: true,
})

const text = value => value == null ? "" : String(value)
// A product as the form edits it. A rule per place counts the places from 1 up to its last one.
export const formOf = product => ({
  name: product.name, en: text(product.names?.en), bg: text(product.names?.bg), family: product.family,
  sport: product.sports?.length === 1 ? product.sports[0] : "all", allCategories: !product.categories, categories: product.categories || [],
  officialBalls: product.officialBalls || [], tenis10Levels: product.tenis10Levels || [],
  unit: product.unit, perUnit: text(product.perUnit), groupingName: text(product.grouping?.name), groupingUnits: text(product.grouping?.units),
  unitWeight: text(product.unitWeight),
  supplier: product.supplier, minimum: text(product.minimum), expiresOn: text(product.expiresOn),
  ruleType: product.rule?.type || "", places: text(product.rule?.places ? Math.max(...product.rule.places) : 2), reference: text(product.rule?.product),
  factor: text(product.rule?.factor), quantity: text(product.rule?.quantity), gameType: text(product.rule?.gameType), reserve: text(product.reserve ?? 0),
  active: product.active,
})

// A number the text holds, within [min, max]; `above` excludes the minimum.
const number = (value, { min = 0, max, whole = false, above = false }) => (whole ? /^\d+$/ : /^\d+(\.\d+)?$/).test(value.trim())
  && (above ? Number(value) > min : Number(value) >= min) && Number(value) <= max

// What the form checks before the preview, by field, with the catalogue's limits. The catalogue's own
// rules (the product a percentage refers to) are Events' answer to the preview.
export const formErrors = form => {
  const errors = {}
  if (!form.name.trim()) errors.name = "Enter a name."
  if (form.family === "lanyards" && !isTenis10Form(form)) errors.family = "Lanyards are Tenis10's: choose Tenis10 as the sport."
  if (!isTenis10Form(form) && !form.allCategories && !form.categories.length) errors.categories = "Choose at least one category, or all of them."
  if (!unitsOf(form).includes(form.unit)) errors.unit = "Choose one of the units: the catalogue no longer counts in this one."
  if (!number(form.perUnit, { min: 1, max: LIMITS.perUnit, whole: true })) errors.perUnit = `Enter a whole number from 1 to ${LIMITS.perUnit}.`
  if (form.groupingUnits.trim() && !form.groupingName.trim()) errors.groupingName = "Name the package, or clear its units."
  if (form.groupingName.trim() && !number(form.groupingUnits, { min: 2, max: LIMITS.packageUnits, whole: true })) {
    errors.groupingUnits = `Enter 2 to ${LIMITS.packageUnits} units per package.`
  }
  if (form.unitWeight.trim() && !number(form.unitWeight, { max: LIMITS.weight })) {
    errors.unitWeight = `Enter a weight in kg, at most ${LIMITS.weight} (for example 0.26).`
  }
  if (form.minimum.trim() && !number(form.minimum, { max: LIMITS.quantity, whole: true })) {
    errors.minimum = `Enter a whole number, at most ${LIMITS.quantity}.`
  }
  if (form.ruleType === "percentOf") {
    if (!form.reference) errors.reference = "Choose the product the percentage refers to."
    if (!number(form.factor, { max: LIMITS.factor, above: true })) errors.factor = `Enter a factor above 0, at most ${LIMITS.factor}.`
  }
  if (form.ruleType === "perGroup" && !groupsRule(form)) errors.ruleType = "R8 counts the club's Tenis10 balls: choose Tenis10 balls, or another rule."
  if (QUANTITY_RULES[form.ruleType] && !number(form.quantity, { max: LIMITS.quantity, above: true })) {
    errors.quantity = `Enter a quantity above 0, at most ${LIMITS.quantity}.`
  }
  if (form.ruleType && !number(form.reserve, { max: LIMITS.reserve })) errors.reserve = "Enter a percentage from 0 to 100."
  return errors
}

const ruleOf = form => {
  const type = form.ruleType
  if (!type) return null
  if (type === "perNewBallMatch") return { type }
  if (type === "perPlace") return { type, places: Array.from({ length: Number(form.places) }, (_, index) => index + 1) }
  if (type === "percentOf") return { type, product: form.reference, factor: Number(form.factor) }
  if (type === "fixed" || type === "perGroup") return { type, quantity: Number(form.quantity) }
  return { type, quantity: Number(form.quantity), ...(form.gameType ? { gameType: form.gameType } : {}) }
}
const numberOrNull = value => value.trim() === "" ? null : Number(value)

// The product the API receives. Only balls cover official balls; every product is held in stock, so any
// may have a threshold and an expiry date; without a rule, the reserve is 0. A Tenis10 product has no event categories and covers no
// official ball: a ball or a lanyard names its colours in the programme's order, none for one added only by hand (the tricolour lanyards).
export const productOf = form => ({
  name: form.name.trim(),
  names: { en: form.en.trim() || null, bg: form.bg.trim() || null },
  family: form.family,
  sports: form.sport === "all" ? null : [form.sport],
  categories: form.allCategories || isTenis10Form(form) ? null : form.categories,
  officialBalls: form.family === "balls" && !isTenis10Form(form) ? form.officialBalls : [],
  ...(isTenis10Form(form) ? { tenis10Levels: followsColours(form) && form.tenis10Levels.length ? COLOURS.filter(level => form.tenis10Levels.includes(level)) : null } : {}),
  unit: form.unit,
  perUnit: Number(form.perUnit),
  grouping: form.groupingName.trim() ? { name: form.groupingName.trim(), units: Number(form.groupingUnits) } : null,
  unitWeight: numberOrNull(form.unitWeight),
  supplier: form.supplier,
  minimum: numberOrNull(form.minimum),
  expiresOn: form.expiresOn || null,
  rule: ruleOf(form),
  reserve: form.ruleType ? Number(form.reserve) : 0,
  active: form.active,
})
